import { Picture } from '@cx-web/components/primitives/Picture';

import { useLayoutStore } from '~/providers/layout-store-provider';
import { type CmpProps, useCmpProps } from '~/utils/cmp';
import { formatLinkProperties } from '~/utils/formatLinkProperties';

import begambleaware from './img/compliance/begambleaware.svg';
import gamblingtherapy from './img/compliance/gambling-therapy.svg';
import gamstop from './img/compliance/gamstop.svg';
import raigSvg from './img/compliance/raig.svg';
import logo from './img/oddschecker.svg';
import facebook from './img/social/facebook.svg';
import instagram from './img/social/instagram.svg';
import snapchat from './img/social/snapchat.svg';
import twitter from './img/social/twitter.svg';
import youtube from './img/social/youtube.svg';
import { FooterLink } from './FooterLink';
import styles from './FooterOC.module.css';

type LinkProps = {
  link?: string;
  name: string;
  onClick?: (e: React.MouseEvent) => void;
};

const staticLinks: LinkProps[] = [
{ name: 'About Us', link: 'https://support.oddschecker.com/hc/en-us' },
{ name: 'Advertising', link: 'mailto:marketing@oddschecker.com' },
{ name: 'Contact Us', link: 'https://support.oddschecker.com/hc/en-us/requests/new' },
{ name: 'Help', link: 'https://support.oddschecker.com/hc/en-us' },
{ name: 'Casino', link: 'casino' },
{ name: 'Careers', link: 'https://careers.oddschecker.com/' },
{ name: 'Terms and Conditions', link: 'myoddschecker/terms-and-conditions' },
{ name: 'Privacy & Cookies', link: 'myoddschecker/cookies' }];


const mobileLink: LinkProps = { name: 'Mobile', link: 'https://www.oddschecker.com/app' };

function getFooterLinks(isCmpEnabled: boolean, cmpProps: CmpProps): LinkProps[] {
  const injectedLinks: LinkProps[] =
  isCmpEnabled && cmpProps ? [{ name: cmpProps.name, onClick: cmpProps.onClick }] : [];
  return [...staticLinks, ...injectedLinks, mobileLink];
}

const socialLinks = [
{
  title: 'Facebook',
  href: 'https://www.facebook.com/OddscheckerBetting',
  svgSrc: facebook
},
{
  title: 'Instagram',
  href: 'https://www.instagram.com/oddscheckertv',
  svgSrc: instagram
},
{
  title: 'Snapchat',
  href: 'https://www.snapchat.com/add/OddscheckerUK',
  svgSrc: snapchat
},
{
  title: 'YouTube',
  href: 'https://www.youtube.com/user/Oddschecker',
  svgSrc: youtube
},
{
  title: 'Twitter',
  href: 'https://twitter.com/oddschecker',
  svgSrc: twitter
}];


export const FooterOC = (): JSX.Element => {
  const isCmpEnabled = useLayoutStore((s) => s.isAdmiralCookieBannerEnabled);
  const cmpProps = useCmpProps();
  const links = getFooterLinks(isCmpEnabled, cmpProps);

  return (
    <section className={styles.footerContainer} data-testid="footer-oc">
      <div className={`container ${styles.footerWrapper}`}>
        <div className={styles.footerCenter}>
          <a data-testid="oc-logo-link" {...formatLinkProperties('https://www.oddschecker.com/')}>
            <Picture altText="oddschecker logo" className={styles.footerLogoLink} svgSrc={logo} />
          </a>
          <div className={styles.footerLinksWrapper}>
            <div className={styles.socialLogosWrapper} data-testid="social-logos">
              {socialLinks.map(({ href, svgSrc, title }) =>
              <a href={href} key={title} rel="noopener noreferrer" target="_blank" title={title}>
                  <Picture
                  altText={`${title.toLowerCase()} icon`}
                  className={styles.socialIcon}
                  height={40}
                  svgSrc={svgSrc}
                  width={40} />

                </a>
              )}
            </div>
            <div className={styles.standardLinksContainer} data-testid="general-links">
              <ul className={styles.standardLinksList}>
                {links.map((link, idx) =>
                <FooterLink
                  key={link.name.toLowerCase()}
                  link={link.link}
                  name={link.name}
                  onClick={link.onClick}
                  showSeparator={idx > 0} />

                )}
              </ul>
            </div>
          </div>
        </div>
        <div className={styles.logosWrapper} data-testid="logo-links">
          <a href="https://www.gamstop.co.uk/" rel="noopener noreferrer" target="_blank" title="GamStop">
            <Picture altText="gamstop icon" className={styles.gamStop} height={13} svgSrc={gamstop} width={54} />
          </a>
          <a href="https://www.begambleaware.org/" rel="noopener noreferrer" target="_blank" title="BeGambleAware">
            <Picture
              altText="gamble aware icon"
              className={styles.gambleAware}
              height={22}
              svgSrc={begambleaware}
              width={116} />

          </a>
          <a href="https://www.raig.org/" rel="noopener noreferrer" target="_blank" title="Raig">
            <Picture altText="raig icon" className={styles.raig} height={18} svgSrc={raigSvg} width={55} />
          </a>
          <a
            href="https://www.gamblingtherapy.org/en"
            rel="noopener noreferrer"
            target="_blank"
            title="Gambling Therapy">

            <Picture
              altText="gambling therapy icon"
              className={styles.gamblingTherapy}
              height={22}
              svgSrc={gamblingtherapy}
              width={60} />

          </a>
        </div>
      </div>
    </section>);

};